import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getCurrentProject,
  getProjectIdFromUrl,
  setCurrentProject,
  redirectIfNoProject,
  isProjectRequiredRoute,
} from "../utils/projectUtils";
import { onProjectChange, forceSyncProject } from "../utils/urlProjectSync";

/**
 * Higher-order component that automatically handles project URL parameters
 * and ensures components have access to the current project
 */
export const withProject = (WrappedComponent) => {
  return function WithProjectComponent(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      const initializeProject = async () => {
        // Check if current route requires a project and redirect if needed
        if (redirectIfNoProject(navigate)) {
          return;
        }

        // Get project from URL parameters or localStorage
        const urlProjectId = getProjectIdFromUrl();
        let currentProject = getCurrentProject();

        // Get project based on URL parameter
        if (urlProjectId) {
          currentProject = getCurrentProject(); // This now uses URL parameter internally

          if (!currentProject) {
            // Project not found in storage, redirect to projects page
            navigate("/projects");
            return;
          }
        }

        // If no project ID in URL, redirect to projects page (since we need URL parameter now)
        if (!urlProjectId) {
          navigate("/projects");
          return;
        }

        setProject(currentProject);
        setIsLoading(false);
      };

      initializeProject();
    }, [location.search, navigate]);

    // Show loading while initializing
    if (isLoading) {
      return <div>Loading...</div>;
    }

    // Pass project as prop to wrapped component
    return <WrappedComponent {...props} project={project} />;
  };
};

/**
 * Hook to get current project with URL parameter synchronization
 * This hook ensures the component re-renders when the URL project parameter changes
 */
export const useProject = () => {
  const [project, setProject] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const updateProject = () => {
      // Check if current route requires a project and redirect if needed
      if (redirectIfNoProject(navigate)) {
        return;
      }

      const currentProject = getCurrentProject();
      setProject(currentProject);
    };

    updateProject();

    // Force sync project from URL when location changes
    forceSyncProject();

    // Listen for project changes from URL sync
    const unsubscribe = onProjectChange((newProject) => {
      setProject(newProject);
    });

    return unsubscribe;
  }, [location.search, navigate]);

  const updateProject = (newProject) => {
    setCurrentProject(newProject, true);
    setProject(newProject);
    // Force sync to ensure Redux store is updated
    forceSyncProject();
  };

  return {
    project,
    setProject: updateProject,
    getCurrentProject,
  };
};

/**
 * Hook specifically for getting current project that updates with URL changes
 * Simpler version that just tracks the current project
 */
export const useCurrentProject = () => {
  const [project, setProject] = useState(() => getCurrentProject());
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Update project when URL changes
    const currentProject = getCurrentProject();
    const projectIdFromUrl = getProjectIdFromUrl();

    // Only redirect if there's a project ID in URL but it's invalid
    if (projectIdFromUrl && !currentProject && isProjectRequiredRoute()) {
      console.warn("Invalid project ID in URL:", projectIdFromUrl);
      setProject(null);
      navigate("/projects");
    } else if (
      currentProject &&
      (!currentProject._id || typeof currentProject._id !== "string")
    ) {
      console.warn("Invalid project detected:", currentProject);
      setProject(null);
      // Only redirect if current route requires a project
      if (isProjectRequiredRoute()) {
        navigate("/projects");
      }
    } else {
      setProject(currentProject);
    }

    // Force sync Redux store
    forceSyncProject();

    // Listen for project changes
    const unsubscribe = onProjectChange((newProject) => {
      // Only redirect if there's a project ID in URL but the project is invalid
      if (projectIdFromUrl && !newProject && isProjectRequiredRoute()) {
        console.warn("Invalid project received from URL sync:", newProject);
        setProject(null);
        navigate("/projects");
      } else if (
        newProject &&
        (!newProject._id || typeof newProject._id !== "string")
      ) {
        console.warn(
          "Invalid project structure received from URL sync:",
          newProject
        );
        setProject(null);
        // Only redirect if current route requires a project
        if (isProjectRequiredRoute()) {
          navigate("/projects");
        }
      } else {
        setProject(newProject);
      }
    });

    return unsubscribe;
  }, [location.search, location.pathname, navigate]);

  return project;
};
